import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus,
  Search,
  Edit,
  Trash2,
  FileText,
  Database,
  Bot,
  GitBranch,
  Settings,
  Filter,
  Download,
  Upload,
  AlertTriangle
} from 'lucide-react';
import { KnowledgeBase, KnowledgeBaseCreate, KnowledgeBaseUpdate } from '../../types';
import apiService from '../../services/api';
import Button from '../common/Button';
import Modal from '../common/Modal';
import LoadingSpinner from '../common/LoadingSpinner';
import DocumentUploadModal from './DocumentUploadModal';
import CustomSelect from '../common/CustomSelect';

interface KnowledgeBasePageProps {
  projectId?: number;
}

const KnowledgeBasePage: React.FC<KnowledgeBasePageProps> = ({ projectId }) => {
  const [knowledgeBases, setKnowledgeBases] = useState<KnowledgeBase[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [contentTypeFilter, setContentTypeFilter] = useState<string>('all');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [selectedKnowledgeBase, setSelectedKnowledgeBase] = useState<KnowledgeBase | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<KnowledgeBase | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);

  // Form states
  const [formData, setFormData] = useState<KnowledgeBaseCreate>({
    name: '',
    description: '',
    content_type: 'general',
    content_text: '',
    content_metadata: {}
  });

  const [searchFormData, setSearchFormData] = useState({
    query: '',
    content_types: [] as string[],
    limit: 10,
    similarity_threshold: 0.7
  });

  useEffect(() => {
    loadKnowledgeBases();
  }, [projectId]);

  const loadKnowledgeBases = async () => {
    try {
      setLoading(true);
      setError(null);
      const params: any = {};
      if (projectId) {
        params.project_id = projectId;
      }
      if (contentTypeFilter !== 'all') {
        params.content_type = contentTypeFilter;
      }
      
      const data = await apiService.getKnowledgeBases(params);
      setKnowledgeBases(data);
    } catch (err) {
      setError('Failed to load knowledge base entries');
      console.error('Error loading knowledge bases:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateKnowledgeBase = async () => {
    try {
      setLoading(true);
      const newKnowledgeBase = await apiService.createKnowledgeBase({
        ...formData,
        project_id: projectId
      });
      setKnowledgeBases(prev => [...prev, newKnowledgeBase]);
      setIsCreateModalOpen(false);
      resetForm();
    } catch (err) {
      setError('Failed to create knowledge base entry');
      console.error('Error creating knowledge base:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateKnowledgeBase = async () => {
    if (!selectedKnowledgeBase) return;
    
    try {
      setLoading(true);
      const updatedKnowledgeBase = await apiService.updateKnowledgeBase(
        selectedKnowledgeBase.knowledge_base_id,
        {
          name: formData.name,
          description: formData.description,
          content_text: formData.content_text,
          content_metadata: formData.content_metadata
        }
      );
      setKnowledgeBases(prev => 
        prev.map(kb => 
          kb.knowledge_base_id === selectedKnowledgeBase.knowledge_base_id 
            ? updatedKnowledgeBase 
            : kb
        )
      );
      setIsEditModalOpen(false);
      resetForm();
    } catch (err) {
      setError('Failed to update knowledge base entry');
      console.error('Error updating knowledge base:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteKnowledgeBase = (knowledgeBaseId: number) => {
    const target = knowledgeBases.find(kb => kb.knowledge_base_id === knowledgeBaseId);
    if (target) {
      setDeleteTarget(target);
    }
  };

  const confirmDeleteKnowledgeBase = async () => {
    if (!deleteTarget) return;

    try {
      setDeleteLoading(true);
      await apiService.deleteKnowledgeBase(deleteTarget.knowledge_base_id);
      setKnowledgeBases(prev => prev.filter(kb => kb.knowledge_base_id !== deleteTarget.knowledge_base_id));
      setDeleteTarget(null);
    } catch (err) {
      setError('Failed to delete knowledge base entry');
      console.error('Error deleting knowledge base:', err);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleSearchKnowledgeBase = async () => {
    try {
      setSearchLoading(true);
      const results = await apiService.searchKnowledgeBase({
        query: searchFormData.query,
        user_id: 1, // This should come from auth context
        project_id: projectId,
        content_types: searchFormData.content_types.length > 0 ? searchFormData.content_types : undefined,
        limit: searchFormData.limit,
        similarity_threshold: searchFormData.similarity_threshold
      });
      setSearchResults(results);
    } catch (err) {
      setError('Failed to search knowledge base');
      console.error('Error searching knowledge base:', err);
    } finally {
      setSearchLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      content_type: 'general',
      content_text: '',
      content_metadata: {}
    });
  };

  // Handle document upload completion - refresh the list
  const handleUploadComplete = useCallback(() => {
    loadKnowledgeBases();
  }, [projectId, contentTypeFilter]);

  const openEditModal = (knowledgeBase: KnowledgeBase) => {
    setSelectedKnowledgeBase(knowledgeBase);
    setFormData({
      name: knowledgeBase.name,
      description: knowledgeBase.description || '',
      content_type: knowledgeBase.content_type,
      content_text: knowledgeBase.content_text,
      content_metadata: knowledgeBase.content_metadata || {}
    });
    setIsEditModalOpen(true);
  };

  const getContentTypeIcon = (contentType: string) => {
    switch (contentType) {
      case 'project':
        return <Database className="w-4 h-4" />;
      case 'orchestration':
        return <GitBranch className="w-4 h-4" />;
      case 'agent':
        return <Bot className="w-4 h-4" />;
      case 'model':
        return <Settings className="w-4 h-4" />;
      default:
        return <FileText className="w-4 h-4" />;
    }
  };

  const getContentTypeLabel = (contentType: string) => {
    switch (contentType) {
      case 'orchestration':
        return 'Workflow';
      case 'project':
        return 'Project';
      case 'agent':
        return 'Agent';
      case 'model':
        return 'Model';
      case 'document':
        return 'Document';
      default:
        return contentType;
    }
  };

  const getContentTypeColor = (contentType: string) => {
    switch (contentType) {
      case 'project':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
      case 'orchestration':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
      case 'agent':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300';
      case 'model':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
    }
  };

  const filteredKnowledgeBases = knowledgeBases.filter(kb => {
    const matchesSearch = kb.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         kb.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         kb.content_text.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = contentTypeFilter === 'all' || kb.content_type === contentTypeFilter;
    return matchesSearch && matchesType;
  });

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-warm-white mb-2">
                Knowledge Base
              </h1>
              <p className="text-gray-600 dark:text-gray-400">
                Manage and search your AI assistant's knowledge base
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Button
                onClick={() => setIsSearchModalOpen(true)}
                className="flex items-center space-x-2 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 bg-white dark:bg-cap-dark hover:bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-900 rounded-lg min-h-[48px]"
              >
                <Search className="w-4 h-4" />
                <span>Search</span>
              </Button>
              <Button
                onClick={() => setIsUploadModalOpen(true)}
                className="flex items-center space-x-2 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 bg-white dark:bg-cap-dark hover:bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-900 rounded-lg min-h-[48px]"
              >
                <Upload className="w-4 h-4" />
                <span className="hidden sm:inline">Upload Documents</span>
                <span className="sm:hidden">Upload</span>
              </Button>
              <Button
                onClick={() => setIsCreateModalOpen(true)}
                className="flex items-center space-x-2 min-h-[48px]"
              >
                <Plus className="w-4 h-4" />
                <span>Add Entry</span>
              </Button>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white dark:bg-cap-dark p-4 rounded-lg border border-gray-200 dark:border-gray-700 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search knowledge base entries..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-800 dark:text-warm-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Filter className="w-4 h-4 text-gray-400" />
              <CustomSelect
                value={contentTypeFilter}
                onChange={(value) => setContentTypeFilter(value)}
                options={[
                  { value: 'all', label: 'All Types' },
                  { value: 'project', label: 'Operations' },
                  { value: 'orchestration', label: 'Workflows' },
                  { value: 'agent', label: 'Agents' },
                  { value: 'model', label: 'Models' },
                  { value: 'general', label: 'General' },
                ]}
              />
            </div>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6">
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {/* Knowledge Base Entries */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredKnowledgeBases.map((knowledgeBase) => (
            <div
              key={knowledgeBase.knowledge_base_id}
              className="bg-white dark:bg-cap-dark p-4 sm:p-6 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-2">
                  <div className={`p-2 rounded-lg ${getContentTypeColor(knowledgeBase.content_type)}`}>
                    {getContentTypeIcon(knowledgeBase.content_type)}
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-warm-white">{knowledgeBase.name}</h3>
                    <span className={`inline-block px-2 py-1 text-xs rounded-full ${getContentTypeColor(knowledgeBase.content_type)}`}>
                      {getContentTypeLabel(knowledgeBase.content_type)}
                    </span>
                  </div>
                </div>
                <div className="flex space-x-1">
                  <button
                    onClick={() => openEditModal(knowledgeBase)}
                    className="p-2 text-gray-400 hover:text-gray-600 dark:text-gray-400 min-w-[44px] min-h-[44px] flex items-center justify-center"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteKnowledgeBase(knowledgeBase.knowledge_base_id)}
                    className="p-2 text-gray-400 hover:text-red-600 min-w-[44px] min-h-[44px] flex items-center justify-center"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              
              {knowledgeBase.description && (
                <p className="text-gray-600 dark:text-gray-400 text-sm mb-3">{knowledgeBase.description}</p>
              )}
              
              <div className="text-sm text-gray-500 dark:text-gray-400">
                <p className="line-clamp-2">{knowledgeBase.content_text.substring(0, 200)}</p>
              </div>
              
              <div className="mt-4 text-xs text-gray-400">
                Created: {new Date(knowledgeBase.created_at).toLocaleDateString()}
              </div>
            </div>
          ))}
        </div>

        {filteredKnowledgeBases.length === 0 && !loading && (
          <div className="text-center py-12">
            <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 dark:text-gray-400">No knowledge base entries found</p>
            <p className="text-sm text-gray-400 mt-1">
              {searchQuery || contentTypeFilter !== 'all' 
                ? 'Try adjusting your search or filters' 
                : 'Create your first knowledge base entry to get started'}
            </p>
          </div>
        )}
      </div>

      {/* Create Modal */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
      >
        <div className="p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-warm-white mb-4">Add Knowledge Base Entry</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Name
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-800 dark:text-warm-white px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Enter name"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Description
              </label>
              <input
                type="text"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-800 dark:text-warm-white px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Enter description"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Content Type
              </label>
              <CustomSelect
                value={formData.content_type}
                onChange={(value) => setFormData({ ...formData, content_type: value as any })}
                options={[
                  { value: 'general', label: 'General' },
                  { value: 'project', label: 'Project' },
                  { value: 'orchestration', label: 'Workflow' },
                  { value: 'agent', label: 'Agent' },
                  { value: 'model', label: 'Model' },
                ]}
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Content
              </label>
              <textarea
                value={formData.content_text}
                onChange={(e) => setFormData({ ...formData, content_text: e.target.value })}
                rows={4}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-800 dark:text-warm-white px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Enter content"
              />
            </div>
          </div>
          
          <div className="flex justify-end space-x-3 mt-6">
            <Button
              onClick={() => setIsCreateModalOpen(false)}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 bg-white dark:bg-cap-dark hover:bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-900 rounded-lg"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateKnowledgeBase}
              disabled={!formData.name || !formData.content_text}
              className="px-4 py-2 bg-blue-500 text-white hover:bg-primary-600 rounded-lg disabled:opacity-50"
            >
              Create
            </Button>
          </div>
        </div>
      </Modal>

      {/* Edit Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
      >
        <div className="p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-warm-white mb-4">Edit Knowledge Base Entry</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Name
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-800 dark:text-warm-white px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Description
              </label>
              <input
                type="text"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-800 dark:text-warm-white px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Content
              </label>
              <textarea
                value={formData.content_text}
                onChange={(e) => setFormData({ ...formData, content_text: e.target.value })}
                rows={4}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-800 dark:text-warm-white px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </div>
          
          <div className="flex justify-end space-x-3 mt-6">
            <Button
              onClick={() => setIsEditModalOpen(false)}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 bg-white dark:bg-cap-dark hover:bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-900 rounded-lg"
            >
              Cancel
            </Button>
            <Button
              onClick={handleUpdateKnowledgeBase}
              disabled={!formData.name || !formData.content_text}
              className="px-4 py-2 bg-blue-500 text-white hover:bg-primary-600 rounded-lg disabled:opacity-50"
            >
              Update
            </Button>
          </div>
        </div>
      </Modal>

      {/* Search Modal */}
      <Modal
        isOpen={isSearchModalOpen}
        onClose={() => setIsSearchModalOpen(false)}
      >
        <div className="p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-warm-white mb-4">Search Knowledge Base</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Search Query
              </label>
              <input
                type="text"
                value={searchFormData.query}
                onChange={(e) => setSearchFormData({ ...searchFormData, query: e.target.value })}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-800 dark:text-warm-white px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Enter your search query"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Content Types (optional)
              </label>
              <div className="space-y-2">
                {['project', 'orchestration', 'agent', 'model', 'general'].map((type) => (
                  <label key={type} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={searchFormData.content_types.includes(type)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSearchFormData({
                            ...searchFormData,
                            content_types: [...searchFormData.content_types, type]
                          });
                        } else {
                          setSearchFormData({
                            ...searchFormData,
                            content_types: searchFormData.content_types.filter(t => t !== type)
                          });
                        }
                      }}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">{getContentTypeLabel(type)}</span>
                  </label>
                ))}
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Limit
                </label>
                <input
                  type="number"
                  value={searchFormData.limit}
                  onChange={(e) => setSearchFormData({ ...searchFormData, limit: parseInt(e.target.value) })}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-800 dark:text-warm-white px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  min="1"
                  max="50"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Similarity Threshold
                </label>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  max="1"
                  value={searchFormData.similarity_threshold}
                  onChange={(e) => setSearchFormData({ ...searchFormData, similarity_threshold: parseFloat(e.target.value) })}
                  className="w-full border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-800 dark:text-warm-white px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>
          
          <div className="flex justify-end space-x-3 mt-6">
            <Button
              onClick={() => setIsSearchModalOpen(false)}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 bg-white dark:bg-cap-dark hover:bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-900 rounded-lg"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSearchKnowledgeBase}
              disabled={!searchFormData.query}
              className="px-4 py-2 bg-blue-500 text-white hover:bg-primary-600 rounded-lg disabled:opacity-50"
            >
              {searchLoading ? <LoadingSpinner size="sm" /> : 'Search'}
            </Button>
          </div>

          {/* Search Results */}
          {searchResults.length > 0 && (
            <div className="mt-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-warm-white mb-4">Search Results</h3>
              <div className="space-y-3">
                {searchResults.map((result, index) => (
                  <div key={index} className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium text-gray-900 dark:text-warm-white">{result.name}</h4>
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        Score: {(result.similarity_score * 100).toFixed(1)}%
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">{result.content_text}</p>
                    <div className="flex items-center space-x-2">
                      <span className={`inline-block px-2 py-1 text-xs rounded-full ${getContentTypeColor(result.content_type)}`}>
                        {getContentTypeLabel(result.content_type)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </Modal>

      {/* Document Upload Modal */}
      <DocumentUploadModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        projectId={projectId}
        onUploadComplete={handleUploadComplete}
      />

      {/* Delete Confirmation Dialog */}
      {deleteTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-cap-dark rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center space-x-3 mb-4">
              <AlertTriangle className="w-6 h-6 text-red-500" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-warm-white">Delete Knowledge Entry</h3>
            </div>

            <div className="mb-6">
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                Are you sure you want to delete <strong>"{deleteTarget.name}"</strong>?
              </p>

              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-4">
                <div className="flex items-start space-x-2">
                  <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-red-800 dark:text-red-300 mb-1">
                      This will permanently delete:
                    </p>
                    <ul className="text-sm text-red-700 dark:text-red-400 space-y-1">
                      <li>The knowledge entry and its content</li>
                      <li>Associated vector embeddings</li>
                    </ul>
                  </div>
                </div>
              </div>

              <p className="text-sm text-gray-600 dark:text-gray-400">
                This action cannot be undone.
              </p>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteTarget(null)}
                disabled={deleteLoading}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-cap-dark border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteKnowledgeBase}
                disabled={deleteLoading}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-lg hover:bg-red-700 focus:ring-2 focus:ring-red-500 focus:border-transparent disabled:opacity-50"
              >
                {deleteLoading ? 'Deleting...' : 'Delete Entry'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KnowledgeBasePage; 